<template>
    <article class="product-container" :data-testing="item.variation.id">
        <add-to-wish-list class="d-none" :short="true" :variation-id="item.variation.id"></add-to-wish-list>
        <div class="product-pictures">
            <item-store-special
                :recommended-retail-price="item.prices.rrp"
                :variation-retail-price="item.prices.default"
                :decimal-count="decimalCount"
                :availability="item.variation.availabilityId"
                :current-shop-sale="showCurrentSaleTag"
            ></item-store-special>


            <!-- ADD TO BASKET -->
            <slot name="add-to-basket">
                <add-to-basket
                    data-testing="item-add-to-basket"
                    :variation-id="item.variation.id"
                    :is-salable="!!item.filter && item.filter.isSalable"
                    :has-children="!!item.item && item.item.salableVariationCount > 1"
                    :interval-quantity="item.variation.intervalOrderQuantity || 1"
                    :minimum-quantity="item.variation.minimumOrderQuantity"
                    :maximum-quantity="!!item.variation.maximumOrderQuantity && item.variation.maximumOrderQuantity > 0 ? item.variation.maximumOrderQuantity : null"
                    :order-properties="item.properties.filter(function(prop) { return prop.property.isOderProperty })"
                    :has-order-properties="item.hasOrderProperties"
                    :has-required-order-property="item.hasRequiredOrderProperty"
                    :use-large-scale="false"
                    :button-size="'sm'"
                    :show-quantity="false"
                    :item-url="item | itemURL(urlWithVariationId)"
                    :has-price="item | hasItemDefaultPrice"
                    :has-graduated-price="itemGraduatedPriceisCheapestSorting || itemGraduatedPricesalableVariationCount"
                    :item-type="item.item.itemType">
                </add-to-basket>
            </slot>
            <!-- ./ ADD TO BASKET -->

            <slot name="item-image">
                <category-image-carousel :image-urls-data="item.images | itemImages(imageUrlAccessor)"
                                        :alt="item | itemName"
                                        :title="item | itemName"
                                        :item-url="item | itemURL(urlWithVariationId)"
                                        :enable-carousel="$ceres.config.item.enableImageCarousel"
                                        :disable-carousel-on-mobile="disableCarouselOnMobile"
                                        ref="categoryImageCarousel">
                </category-image-carousel>
            </slot>
        </div>




        <!-- ITEM DETAILS -->
        <slot name="product-details">
            <div class="product-meta">
                <span class="brand" v-html="brandName"></span>
                <div class="prices">
                        <div v-if="item.prices.rrp && item.prices.rrp.price.value > 0 && item.prices.rrp.price.value > item.prices.default.price.value" class="crossPrice">
                            <del v-if="item.prices.specialOffer">
                                {{ item.prices.default.unitPrice.formatted }}
                            </del>
                            <del v-else>
                                {{ item.prices.rrp.unitPrice.formatted }}
                            </del>
                        </div>

                        <div class="price" v-html="itemPrice"></div>

                        <div class="category-unit-price small" v-if="!(item.unit.unitOfMeasurement === 'C62' && item.unit.content === 1)">
                            <span>{{ item.unit.content }}</span>
                            <span>&nbsp;{{ item.unit.names.name }}</span>
                            <span v-if="item.variation.mayShowUnitPrice">&nbsp;| {{ basePrice }}</span>
                        </div>
                        
                    </div>
            </div>  
            <a :href="item | itemURL(urlWithVariationId)" class="product-name" v-html="itemName"></a>
            <span class="variationHint" v-if="hasVariations">in mehreren Varianten</span>
        </slot>
        <!-- ./ITEM DETAILS  -->
    </article>
</template>

<script>
import { mapState } from "vuex";
import CategoryImageCarousel from "./CategoryImageCarousel.vue";
import ItemStoreSpecial from "./ItemStoreSpecial.vue";
import {getSlotData} from "../../helper/getSlotData";

export default {
    components: {
        CategoryImageCarousel,
        ItemStoreSpecial
    },

    props: {
        decimalCount:
        {
            type: Number,
            default: 0
        },
        imageUrlAccessor:
        {
            type: String,
            default: "urlMiddle"
        },
        itemData:
        {
            type: Object
        },
        disableCarouselOnMobile:
        {
            type: Boolean
        },
        paddingClasses:
        {
            type: String,
            default: null
        },
        paddingInlineStyles:
        {
            type: String,
            default: null
        },
        forceUrlWithVariationId:
        {
            type: Boolean,
            default: false
        },
        addToBasket:
        {
            type: Boolean,
            default: false
        }
    },

    jsonDataFields: [
        "itemDataRef"
    ],

    computed:
    {
        item()
        {
            return this.itemData || this.itemSlotData || this.itemDataRef;
        },

        itemSlotData: getSlotData('item-data'),

        /**
         * returns itemData.item.storeSpecial
         */
        storeSpecial()
        {
            return this.item.item.storeSpecial;
        },


        itemName() {
            let ret = "";
            if(this.item.texts.name2 && this.item.texts.name2.trim() !== "" && this.item.texts.name2.trim() != "N / A" )
            {
                ret += this.item.texts.name2;
            } else {
                ret += this.item.texts.name1;
            }

            if(this.item.variation.name)
            {
                ret += ' <span class="variationName">' + this.variationName + '</span>';
            }
            return ret;
        },

        /**
         * returns itemData.texts
         */
        texts()
        {
            return this.item.texts;
        },

        brandName() 
        {
            let name = (this.item.item.manufacturer && this.item.item.manufacturer.externalName) ? this.item.item.manufacturer.externalName : "";
            if(name == "BioKinder - Das gesunde Kinderzimmer")
            {
                name = "Naturwohnen";
            }
            return name;
        },

        hasVariations() 
        {
            return (this.groupedAttributes && this.groupedAttributes.length > 0) ? true : false;
        },

        itemPrice()
        {
            let price =  this.$options.filters.specialOffer(this.item.prices.default.unitPrice.formatted, this.item.prices, "unitPrice", "formatted" );
            let priceParts = price.split(',');  // Split the price string by comma
            if (priceParts.length === 2) {
                return `${priceParts[0]},<sup>${priceParts[1]}</sup>`;
            }
            return price;
        },

        basePrice()
        {
            return this.item.prices.default.basePrice;
        },

        itemPriceGraduated()
        {
           let unitPrice;
           if (App.config.item.enableGraduatedPrices) {
             unitPrice = this.item.prices.graduatedPrices.length > 0
                 ? this.item.prices.graduatedPrices[0].unitPrice
                 : this.item.prices.default.unitPrice;
           } else {
             unitPrice = this.item.prices.default.unitPrice;
           }

            return this.$options.filters.specialOffer(unitPrice.formatted, this.item.prices, "unitPrice", "formatted" );
        },

        itemGraduatedPriceisCheapestSorting()
        {
            return !!this.item.item && this.item.item.salableVariationCount > 1 && !!this.$ceres.isCheapestSorting;
        },

        itemGraduatedPricesalableVariationCount()
        {
            return !!this.item.item && this.item.item.salableVariationCount == 1 && this.item.prices.graduatedPrices.length > 1 && App.config.item.enableGraduatedPrices;
        },

        itemSetPrice()
        {
            return this.$options.filters.currency(
                this.item.prices.default.price.value,
                this.item.prices.default.currency
            );
        },
        ourFurniture()
        {
            // 259 = Naturwohnen 17 = BioKinder
            const manufacturerId = this.item.item.manufacturerId;
            if(manufacturerId == 259 || manufacturerId == 17)
            {
                return true;
            }

            if(!this.item.tags)
            {
                return false;
            }
            // 160 = our furniture, 63 = shared furniture with bkm
            return this.item.tags.some(tag => tag.id === 160 || tag.id === 63);
        },
        showCurrentSaleTag()
        {
            const visibleMode = App.nwConfig.shopSale.categoryItemSalesBadge; // 0=No display at all, 1=Furniture, 2=All, 3=CategoryIds
            if (visibleMode == "1" && this.ourFurniture && !this.hasCrossPrice && !this.hasTags([175, 8]))
                return true;
            if (visibleMode == "2" && !this.hasCrossPrice && !this.hasTags([175, 8]))
                return true;

            if (visibleMode == "3")
            {
                const defaultCategories = App.nwConfig.shopSale.salesBadgeCategoryIds ?? "0";

                // comma separated config ids => array of trimmed string ids
                const categoryIds = String(defaultCategories)
                    .split(",")
                    .map(id => id.trim())
                    .filter(id => id.length > 0 && id !== "0");

                if (!categoryIds.length || !Array.isArray(this.item?.defaultCategories))
                    return false;

                // check whether one of the item's default categories matches configured ids
                return this.item.defaultCategories.some(category =>
                    categoryIds.includes(String(category?.id).trim())
                );
            }

            return false;
        },
        urlWithVariationId()
        {
            return !this.$ceres.config.item.showPleaseSelect || this.$ceres.initialPleaseSelect == 0 || this.forceUrlWithVariationId;
        },

        hasCrossPrice() {
            const hasRrpPrice = !!this.item.prices.rrp &&
                this.item.prices.rrp.unitPrice.value > this.item.prices.default.unitPrice.value;

            const hasBeforePrice = !!this.item.prices.specialOffer &&
                !!this.item.prices.default &&
                this.item.prices.default.unitPrice.value > this.item.prices.specialOffer.unitPrice.value;

            return hasRrpPrice || hasBeforePrice;
        },

        variationName() {
            const raw = this.item?.variation?.name || ''
            const decoded = raw.replace(/&szlig;/gi, 'ß')
            return decoded.replace(
                /(^|\s)(Erle|Eiche|Naturweiß)(?=\s|$)/u,
                '$1<span class="variation-first">$2</span>'
            )
        },
        ...mapState({
            showNetPrices: state => state.basket.showNetPrices
        })
    },
    methods: {
        hasTags(tagArray) {
          if (!this.item || !Array.isArray(this.item.tags)) {
            return false;
          }
          
          return this.item.tags
            .map(tag => tag.id)
            .some(id => tagArray.includes(id));
        }
    }
}
</script>
